import { ActionRecord, ActionReference } from "@/shared/domain/actions";
import { Goto } from "@/shared/navigation";
import { RobotoEndpoint } from "@/types";

export function isActionReference(
  record: ActionRecord | ActionReference | null,
): record is ActionReference {
  return (
    record !== null && Object.prototype.hasOwnProperty.call(record, "owner")
  );
}

/**
 * The org that owns an action, whichever way the record at hand spells it: an
 * `ActionReference` carries `owner`, while an `ActionRecord` and an invocation's
 * `provenance.action` carry `org_id`. Empty and null both read as unknown, so callers get
 * `undefined` rather than a falsy string they have to re-check.
 */
function actionOwnerOrgId(
  action: ActionRecord | ActionReference,
): string | undefined {
  const owner = isActionReference(action) ? action.owner : action.org_id;
  return owner ? owner : undefined;
}

/**
 * The action's owning org when that is an org other than `viewerOrgId` (the viewer's current
 * org), and `undefined` when the action is the viewer's own or its owner is unknown.
 *
 * Every Action Hub action returns its owner here, since the hub publishes out of orgs such as
 * `roboto-public` (or `roboto-team` on a private stack) that the viewer is not a member of. So
 * does any other org's action the viewer can see. When `viewerOrgId` is `undefined` (the
 * viewer's current org is not known yet), the owner is reported rather than assumed to be the
 * viewer's, because {@link actionDetailPath} can carry an owner it did not need but cannot
 * recover one it left out.
 */
export function ownerOrgIdIfNotViewers(
  action: ActionRecord | ActionReference,
  viewerOrgId: string | undefined,
): string | undefined {
  const owner = actionOwnerOrgId(action);
  return owner !== undefined && owner !== viewerOrgId ? owner : undefined;
}

/**
 * Path to an action's detail page, which shows the action's latest version. The path names the
 * owner only when it is not the viewer's org (see {@link ownerOrgIdIfNotViewers}).
 *
 * The detail page fetches the action from the org the URL names, or from the viewer's current
 * org when the URL names none, and the service answers 404 for any action that org does not
 * own. An Action Hub action is owned by the org that published it, so a link to it that leaves
 * out the owner reaches nothing.
 */
export function actionDetailPath(
  action: ActionRecord | ActionReference,
  viewerOrgId: string | undefined,
): string {
  const owner = ownerOrgIdIfNotViewers(action, viewerOrgId);

  return Goto.createActionRoute({ name: action.name, owner });
}

export const datasetFilesQueryEndpoint: RobotoEndpoint = (pathParams) => {
  const datasetId = pathParams?.datasetId;

  return `/datasets/${datasetId}/files/query`;
};
